// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color, layoutContainer, xScreen } from "@plumberssupply/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

type Props = OwnProps;

export interface ContentAreaStyles {
    container?: InjectableCss;
    gridContainer?: GridContainerProps;
    gridItem1?: GridItemProps;
    gridItem2?: GridItemProps;
}

export const contentAreaStyles: ContentAreaStyles = {
    container: {
        css: css`
            background: linear-gradient(to top, ${color("common.ui6")} 83%, transparent 83%, transparent 100%);
            ${xScreen("lte", "md")} {
                background: linear-gradient(to top, ${color("common.ui6")} 93%, transparent 93%, transparent 100%);
            }
            ${xScreen("lte", "767")} {
                background-color: ${color("common.ui6")};
            }
        `,
    },
    gridContainer: {
        gap: 22,
        offsetCss: css`
            ${xScreen("lte", "md")} {
                justify-content: center;
                flex-direction: column;
                align-items: center;
            }
        `,
        css: css`
            ${layoutContainer}
            ${xScreen("lte", "md")} {
                padding-bottom: 40px;
            }
            ${xScreen("lte", "767")} {
                padding: 32px 16px;
            }
        `,
    },
    gridItem1: {
        width: [12, 12, 6, 6, 6],
        css: css`
            ${xScreen("lte", "md")} {
                max-width: 638px;
                width: 100%;
                padding-bottom: 25px;
            }
            ${xScreen("lte", "767")} {
                max-width: 330px;
                padding-bottom: 21px;
            }
        `,
    },
    gridItem2: {
        width: [12, 12, 6, 6, 6],
        css: css`
            ${xScreen("lte", "md")} {
                max-width: 638px;
                width: 100%;
            }
            ${xScreen("lte", "767")} {
                max-width: 330px;
            }
        `,
    },
};

const styles = contentAreaStyles;

const ContentArea: FC<Props> = ({ id }) => {
    return (
        <StyledWrapper {...styles.container} data-test-selector="ContentArea">
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.gridItem1}>
                    <Zone contentId={id} zoneName="Content1" />
                </GridItem>
                <GridItem {...styles.gridItem2}>
                    <Zone contentId={id} zoneName="Content2" />
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ContentArea,
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Content Area Widget",
    },
};

export default widgetModule;
